import CloseIcon from '@mui/icons-material/Close';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, Dialog, DialogContent, DialogTitle, Fab, Stack, Tab, Tabs } from "@mui/material";
import IconButton from '@mui/material/IconButton';
import { useMutation } from "@tanstack/react-query";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { NewFormContext } from "../../context/NewFormContext";
import AttToken from "../../helpers/attToken";
import { mensagemErroAtualizacaoUrlPublica } from "../../helpers/fluxo";
import { trackEventMatomo } from '../../helpers/matomo';
import Checklists from "../../service/Checklists";
import Fluxo from "../../service/Fluxo";
import type { ChecklistSubitem, ItemChecklist } from "../../types/checklist/types";
import { ChecklistType } from "../../types/checklist/types";
import { TabPanelProps } from "../../types/helpers/types";
import ChamadosENotificacao from "./ChamadosENotificacao";
import Configuracoes from "./Configuracoes";
import ItensSubitens from "./EditModel";
import PreenchimentoWeb from "./PreenchimentoWeb";
import QrCode from "./QrCodeList";
import Scoring from "./Scoring";
import Associacao from "./Servico";

const modelService = new Checklists();
const fluxoService = new Fluxo();


function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      style={{ width: "100%" }}
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function FormTabs({ originalModelo: oriModelo }: { originalModelo?: ChecklistType }) {
  const { modelo, setModelo, cliente, clienteFormulario, setClienteFormulario, checkedScoring, itemRange, setItemRange, setCheckedScoring } =
    useContext(NewFormContext);
  const { t } = useTranslation("translation");
  const [value, setValue] = useState<number>(0);
  function handleChange(_event: React.SyntheticEvent, newValue: number) {
    trackEventMatomo('edição de formulário', 'click', 'aba', `seção ${newValue + 1}`);
    setValue(newValue);
  }

  //modelo sem alterções para comparação e verificação de alterações
  const [showGoBackModal, setShowGoBackModal] = useState(false);
  const [originalModelo, setOriginalModelo] = useState<ChecklistType | null>(oriModelo ?? null);
  const [originalClienteFormulario, setOriginalClienteFormulario] = useState(clienteFormulario);
  const [originalCheckedScoring, setOriginalCheckedScoring] = useState(checkedScoring);
  const [originalItemRange, setOriginalItemRange] = useState(itemRange);

  const clienteFormularioChanged = JSON.stringify(originalClienteFormulario) !== JSON.stringify(clienteFormulario);

  const hasChanges = React.useMemo(() => {
    const modeloChanged = JSON.stringify(originalModelo) !== JSON.stringify(modelo);

    const checkedScoringChanged = JSON.stringify(originalCheckedScoring) !== JSON.stringify(checkedScoring);

    const itemRangeChanged = JSON.stringify(originalItemRange) !== JSON.stringify(itemRange);

    return modeloChanged || clienteFormularioChanged || checkedScoringChanged || itemRangeChanged;
  }, [modelo, originalModelo, clienteFormularioChanged, checkedScoring, originalCheckedScoring, itemRange, originalItemRange]);

  const [variant, setVariant] = useState<"circular" | "extended">("circular");
  const [variantSave, setVariantSave] = useState<"circular" | "extended">("circular");

  const navigate = useNavigate();

  function routeChange() {
    navigate("/formularios");
    setModelo(null);
    setClienteFormulario(null);
    setItemRange([
      {
        index: 0,
        name: t("formDetails.range_scoring.perfect"),
        range: 100,
      },
    ]);
    setCheckedScoring(false);
  }

  function checkFields(form: ChecklistType): string {
    const itens: ItemChecklist[] = form?.itens ?? [];
    const aliases = form.urlPublica;
    const possuiAlgumAlias = Boolean(aliases?.aliasEmpresa?.trim() || aliases?.aliasChecklist?.trim());
    const possuiAmbosAliases = Boolean(aliases?.aliasEmpresa?.trim() && aliases?.aliasChecklist?.trim());

    const subitens: ChecklistSubitem[] = itens?.flatMap((item) =>
      item.ocorrenciaMultipla
        ? (item.ocorrencias ?? [])?.flatMap((oco) => oco.subitens ?? [])
        : (item.subitens ?? []));
    if (!form.nome?.trim()) return t('checklists.formTabs.validation.formName');
    if (itens?.some((item) => !item.nome?.trim())) return t('checklists.formTabs.validation.itemName');
    if (itens?.some((item) =>
      item.ocorrencias?.some((oco) => !oco.nome?.trim()))) return t('checklists.formTabs.validation.occurrenceName');
    if (subitens.some((sub) => !sub.nome?.trim())) return t('checklists.formTabs.validation.subitemName');
    if (subitens?.some((sub) =>
      sub.tipo === 'avaliacao' &&
      sub.avaliacao?.some((av) => !av.nome?.trim()))) return t('checklists.formTabs.validation.evaluationName');
    if (subitens?.some((sub) => sub.tipo === 'valorMedida' && !sub.subtipo)) return t('checklists.formTabs.validation.measurementValue');
    if (subitens?.some((sub) =>
      (sub.tipo === 'unique' || sub.tipo === 'multiple') &&
      sub.selecao?.some((sel) => !(sel as string)?.trim()))) return ('checklists.formTabs.validation.selectionValue');
    if (possuiAlgumAlias && !possuiAmbosAliases) return t('checklists.publicAccess.invalidAliases');
    if (form.cerca && (
      !Number.isFinite(form.cerca.latitude) || form.cerca.latitude < -90 || form.cerca.latitude > 90 ||
      !Number.isFinite(form.cerca.longitude) || form.cerca.longitude < -180 || form.cerca.longitude > 180 ||
      !Number.isFinite(form.cerca.raioMetros) || form.cerca.raioMetros <= 0
    )) return t('checklists.publicAccess.invalidGeofence');
    return '';
  }

  const { mutate, mutateAsync, isPending } = useMutation({
    mutationKey: ['UPDATE_MODEL'],
    mutationFn: async () => {
      const message = checkFields(modelo as ChecklistType);
      if (message) {
        toast.info(message, { toastId: 'checkFields' });
        throw new Error(message);
      }
      const promise = toast.promise(async () => {
        const token = await AttToken();
        if (token && modelo) {
          if (clienteFormularioChanged && cliente?.id && clienteFormulario) {
            await modelService.editConfigForm(token, modelo.id as string, cliente.id as string, {
              ...clienteFormulario,
              empresa: token.userEmpresa,
              cliente: cliente.id as string,
              formulario: modelo.id as string,
              id: `${cliente.id}_${modelo.id}`,
              distanciaLimite: Number.isFinite(Number(clienteFormulario.distanciaLimite))
                ? Number(clienteFormulario.distanciaLimite)
                : 0,
            });
          }
          if (checkedScoring) {
            await modelService.changeRange(token, itemRange, checkedScoring, modelo?.id as string);
          }
          if ('key' in modelo) {
            delete modelo.key;
          }
          const response = await modelService.changeForm(token, modelo as ChecklistType);
          if (!response.ultimaversao) throw new Error(t('checklists.formTabs.errorUpdatingModel'));
          const aliasEmpresa = modelo.urlPublica?.aliasEmpresa?.trim();
          const aliasChecklist = modelo.urlPublica?.aliasChecklist?.trim();
          if (aliasEmpresa && aliasChecklist) {
            try {
              await fluxoService.atualizarUrlPublicaChecklist(token, modelo.id, { aliasEmpresa, aliasChecklist });
            } catch (error) {
              throw new Error(mensagemErroAtualizacaoUrlPublica(error, t));
            }
          }
          return response;
        }
      }, {
        pending: `${t('checklists.savingChanges')}`,
        success: `${t('checklists.changesSaved')}`,
        error: {
          render({ data }) {
            return data instanceof Error ? data.message : t('checklists.error');
          },
        },
      }, { toastId: 'updateModel' });
      return promise;
    },
    onSuccess: () => {
      trackEventMatomo('edição de formulário', 'sucesso', 'salvamento', 'formulário atualizado');
      setOriginalModelo(structuredClone(modelo));
      setOriginalClienteFormulario(structuredClone(clienteFormulario));
      setOriginalCheckedScoring(structuredClone(checkedScoring));
      setOriginalItemRange(structuredClone(itemRange));
    },
    onError: () => trackEventMatomo('edição de formulário', 'erro', 'salvamento', 'falha ao atualizar formulário'),
  });
  async function handleGoBack(save?: boolean) {
    if (save) {
      try {
        await mutateAsync();
        routeChange();
      } catch (error) {
        console.error(error);
      }
    }
    if (hasChanges) {
      setShowGoBackModal(true);
    } else {
      routeChange();
    }
  }

  return (
    <>
      <Tabs
        value={value}
        onChange={handleChange}
        indicatorColor='secondary'
        textColor='secondary'
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label={t('checklists.formTabs.itemsAndSubitemsEdit')} value={0} />
        <Tab label={`${!modelo?.preenchimentoDeslogado ? t('checklists.formTabs.callsAndNotifications') : t('checklists.formTabs.calls')}`} value={1} />
        {!modelo?.preenchimentoDeslogado && <Tab label={t('checklists.formTabs.settings')} value={2} />}
        {modelo?.preenchimentoDeslogado && <Tab label={t('checklists.formTabs.webFilling')} value={3} />}
        {!modelo?.preenchimentoDeslogado && <Tab label={t('checklists.formTabs.service')} value={4} />}
        <Tab label={t('checklists.formTabs.scoring')} value={5} />
        {!modelo?.preenchimentoDeslogado && <Tab label={t('checklists.formTabs.qrCodeList')} value={6} />}
      </Tabs>
      <Stack direction='row' width='100%' spacing={5}>
        <CustomTabPanel value={value} index={0}>
          <ItensSubitens />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <ChamadosENotificacao />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          <Configuracoes />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={3}>
          <PreenchimentoWeb />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={4}>
          <Associacao mutate={mutate} />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={5}>
          <Scoring />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={6}>
          <QrCode />
        </CustomTabPanel>
        <Stack
          sx={{ position: "fixed", top: 200, right: 20 }}
          height={"200px"}
          direction="column"
          alignItems="flex-end"
          justifyContent="flex-start"
          spacing={1}>
          <Fab
            sx={{ transition: "all .4s" }}
            onMouseEnter={() => setVariant("extended")}
            onMouseLeave={() => setVariant("circular")}
            disabled={isPending}
            onClick={() => handleGoBack()}
            color="primary"
            variant={variant}>
            <KeyboardReturnIcon />
            {variant === 'extended' && t('checklists.formTabs.back')}
          </Fab>
          <Fab
            sx={{ transition: "all .4s" }}
            onMouseEnter={() => setVariantSave("extended")}
            onMouseLeave={() => setVariantSave("circular")}
            disabled={isPending || !hasChanges}
            variant={variantSave}
            onClick={() => {
              trackEventMatomo('edição de formulário', 'click', 'botão', 'salva formulário');
              mutate();
            }}
            color="success">
            <SaveIcon />
            {variantSave === "extended" && t("save")}
          </Fab>
        </Stack>
      </Stack>
      <Dialog
        fullWidth
        maxWidth='sm'
        open={showGoBackModal}
        onClose={() => setShowGoBackModal(false)}
        scroll={'paper'}
      >
        <DialogTitle>{t('checklists.formTabs.unsavedChanges')}</DialogTitle>
        <IconButton
          aria-label={t('checklists.formTabs.close')}
          onClick={() => setShowGoBackModal(false)}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers={true}>
          <Stack direction='row' width='100%' justifyContent='center' spacing={2}>
            <Button onClick={routeChange} color='inherit' variant='outlined' size="large">{t('checklists.formTabs.exitWithoutSave')}</Button>
            <Button startIcon={<SaveIcon />} onClick={() => handleGoBack(true)} color='success' variant='contained' size="large">{t('checklists.formTabs.saveAndExit')}</Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
